import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Save } from 'lucide-react'
import { useAdImageBatchById } from '@/hooks/useAdImageBatchById'
import { useAuth } from '@/hooks/useAuth'
import { adImageBatchService } from '@/services/ad_image_batch_service'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import BatchItemsEditor from './components/BatchItemsEditor'
import { logger } from '@magnetar/utils'

export default function EditBatchPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { token } = useAuth()
  const { batch, isLoading, mutate } = useAdImageBatchById({ batch_id: id || null })

  const [formData, setFormData] = useState({
    batch_name: '',
    description: '',
    overlay_interval_seconds: '60',
    is_active: true,
  })
  const [activeTab, setActiveTab] = useState('info')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (batch) {
      setFormData({
        batch_name: batch.batch_name,
        description: batch.description || '',
        overlay_interval_seconds: String(Math.round(batch.overlay_interval_ms / 1000)),
        is_active: batch.is_active,
      })
    }
  }, [batch])

  const handleSaveInfo = async () => {
    if (!token || !id) return

    setIsSaving(true)
    try {
      const result = await adImageBatchService.updateBatch(
        parseInt(id, 10),
        {
          batch_name: formData.batch_name,
          description: formData.description,
          overlay_interval_ms: (parseInt(formData.overlay_interval_seconds, 10) || 0) * 1000,
          is_active: formData.is_active,
        },
        token,
      )
      if (result.success) {
        mutate()
      }
    } catch (error) {
      logger.error('更新失敗', error)
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (!batch) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">找不到批次</p>
        <Button onClick={() => navigate(-1)} className="mt-4">
          返回列表
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">編輯廣告圖片批次</h1>
          <p className="text-muted-foreground">編輯批次資訊和圖片項目</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="info">基本資訊</TabsTrigger>
          <TabsTrigger value="items">
            圖片項目
            <Badge variant="secondary" className="ml-2">
              {batch.items.filter((item) => item.is_active).length}
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>基本資訊</CardTitle>
              <CardDescription>更新批次的基本資訊</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="batch_name">批次名稱</Label>
                <Input
                  id="batch_name"
                  value={formData.batch_name}
                  onChange={(e) => setFormData({ ...formData, batch_name: e.target.value })}
                  placeholder="輸入批次名稱"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">描述</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="輸入描述"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="overlay_interval_seconds">覆蓋間隔（秒）</Label>
                <Input
                  id="overlay_interval_seconds"
                  type="number"
                  min="1"
                  value={formData.overlay_interval_seconds}
                  onChange={(e) =>
                    setFormData({ ...formData, overlay_interval_seconds: e.target.value })
                  }
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="is_active">啟用批次</Label>
              </div>
              <div className="flex justify-end">
                <Button onClick={handleSaveInfo} disabled={isSaving}>
                  <Save className="mr-2 h-4 w-4" />
                  儲存
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="items" className="space-y-4">
          <BatchItemsEditor batch={batch} onSaved={mutate} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
